import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import useFeatures from "../../../hooks/useFeatures";
import { Button } from "../../../components/ui/Button";
import PatientChatModal from "../../../components/clinical/PatientChatModal";
import { sendBookingLink } from "../../../components/booking/ShareBookingLinkModal";
import { jwtDecode } from "jwt-decode";
import { 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  MoreVertical, 
  Edit3, ClipboardList, MessagesSquare,
  Share2, MessageSquare, Send,
  Copy, 
  Check, 
  ExternalLink, 
  X,
  Calendar,
  User,
  Stethoscope
} from "lucide-react";

export default function Patient() {
  const [patients, setPatients] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [currentUser, setCurrentUser] = useState({ isDoctor: false, userId: null, role: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [shareModal, setShareModal] = useState(null); // { patient, selectedDoctorHashid, selectedDoctorName, isSelfDoctor, link }
  const [copied, setCopied] = useState(false);
  const [sendingVia, setSendingVia] = useState(null); // "whatsapp" | "sms" while sending the link
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("auth");
    if (stored) {
      try {
        const authData = JSON.parse(stored);
        const decoded = authData?.token ? jwtDecode(authData.token) : null;
        const role = (decoded?.sub?.role || authData?.role || "").toLowerCase();
        const userId = decoded?.sub?.user_id || authData?.user_id;
        const isDoctor = role === "doctor";
        setCurrentUser({
          isDoctor,
          userId,
          role,
        });
      } catch (e) {
        console.error("Error decoding auth token:", e);
      }
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await api.fetchPatients({ page: currentPage, limit: 20, search: searchQuery });
        setPatients(res.data.data || []);
        setTotalPages(res.data.pages || 1);
        if (res.data.available_doctors) {
          setAvailableDoctors(res.data.available_doctors);
        }
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentPage, searchQuery]);

  const toggleMenu = (event, id) => {
    if (openMenuId === id) {
      setOpenMenuId(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const right = Math.max(16, window.innerWidth - rect.right);
    const top = rect.bottom + 4;
    const estimatedHeight = 120;
    const adjustedTop = (top + estimatedHeight > window.innerHeight && rect.top > estimatedHeight)
      ? rect.top - estimatedHeight - 4
      : top;

    setMenuPosition({
      top: adjustedTop,
      right: right
    });
    setOpenMenuId(id);
  };

  const handleCloseMenu = () => {
    setOpenMenuId(null);
  };

  // "View record" appears once the patient record page is switched on.
  const features = useFeatures();
  const [chatPatient, setChatPatient] = useState(null);

  async function handlePatientEdit(id) {
    handleCloseMenu();
    try {
      const res = await api.getPatient(id);
      navigate("/patient-edit/", { state: res.data });
    } catch (err) {
      setError("Failed to load patient: " + err);
    }
  }

  const handleOpenShareModal = (patient) => {
    handleCloseMenu();
    const appointmentBaseUrl = process.env.REACT_APP_APPOINTMENT_URL || `${window.location.protocol}//${window.location.hostname}:5174`;

    let selectedHashid = null;
    let selectedName = "Specialist";
    let isSelfDoctor = false;

    if (currentUser.isDoctor && currentUser.userId) {
      // If logged in user is a doctor, pick their details
      const myDoc = availableDoctors.find(d => d.user_id === currentUser.userId || d.id === currentUser.userId);
      if (myDoc) {
        selectedHashid = myDoc.doctor_hashid;
        selectedName = myDoc.name;
        isSelfDoctor = true;
      } else if (patient?.doctor_hashid) {
        selectedHashid = patient.doctor_hashid;
        selectedName = patient.doctor_name;
        isSelfDoctor = true;
      }
    } else {
      // Non-doctor user: pick doctor from patient's latest appointment/consultation (fallback to default)
      if (patient?.doctor_hashid) {
        selectedHashid = patient.doctor_hashid;
        selectedName = patient.doctor_name;
      } else if (availableDoctors.length > 0) {
        selectedHashid = availableDoctors[0].doctor_hashid;
        selectedName = availableDoctors[0].name;
      }
    }

    const link = selectedHashid ? `${appointmentBaseUrl}/book/${selectedHashid}` : `${appointmentBaseUrl}/my-doctors`;

    setShareModal({
      patient,
      selectedDoctorHashid: selectedHashid,
      selectedDoctorName: selectedName,
      isSelfDoctor,
      link
    });
    setCopied(false);
  };

  // Send the link straight to this patient's own number (sendBookingLink:
  // WhatsApp from your own WhatsApp, SMS from MindConnect; recorded as an invite).
  const handleSendLink = async (channel) => {
    const doc = availableDoctors.find(d => d.doctor_hashid === shareModal?.selectedDoctorHashid);
    setSendingVia(channel);
    const done = await sendBookingLink({ channel, doctorUserId: doc?.user_id, phone: shareModal.patient.contact });
    setSendingVia(null);
    if (done && channel === "sms") setShareModal(null);
  };

  const handleDoctorChange = (newHashid) => {
    const doc = availableDoctors.find(d => d.doctor_hashid === newHashid);
    const appointmentBaseUrl = process.env.REACT_APP_APPOINTMENT_URL || `${window.location.protocol}//${window.location.hostname}:5174`;
    const newLink = newHashid ? `${appointmentBaseUrl}/book/${newHashid}` : `${appointmentBaseUrl}/my-doctors`;
    setShareModal(prev => ({
      ...prev,
      selectedDoctorHashid: newHashid,
      selectedDoctorName: doc ? doc.name : (prev?.selectedDoctorName || "Specialist"),
      link: newLink
    }));
    setCopied(false);
  };

  const handleCopyLink = () => {
    if (!shareModal?.link) return;
    navigator.clipboard.writeText(shareModal.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Patients</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" size={18} />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearchQuery(searchInput); setCurrentPage(1); } }}
              className="pl-9 pr-4 py-2 w-full sm:w-64 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <Link to="/patient-new" className="text-blue-600 w-full sm:w-auto">
            <button className="w-full sm:w-auto px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors">
              + New Patient
            </button>
          </Link>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-soft rounded-xl overflow-x-auto border border-secondary-100">
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">Loading patients...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Error: {error}</p>
            </div>
          ) : patients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 font-medium">No patient found.</p>
            </div>
          ) : (
            <>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-secondary-200">
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">ID</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Reg. Date</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Patient Name</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Phone Number</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Address</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Patient Type</th>
                  <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-right pr-6">Action</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p, index) => (
                  <tr key={p.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">#{(currentPage - 1) * 20 + index + 1}</td>
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{p.created}</td>
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{p.name} {p.risk_level && p.risk_level !== "low" && <span title="Current risk level" className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.risk_level === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{p.risk_level} risk</span>}</td>
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{p.contact || "-"}</td>
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{p.address || "-"}</td>
                    <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{p.patient_type}</td>
                    <td className="py-3 px-4 text-sm text-right pr-4">
                      {/* 3 dots action menu button */}
                      <button
                        onClick={(e) => toggleMenu(e, p.id)}
                        className="p-1.5 rounded-lg hover:bg-secondary-100 text-secondary-600 hover:text-secondary-900 transition-colors cursor-pointer"
                        title="Actions"
                      >
                        <MoreVertical size={18} />
                      </button>

                      {/* Dropdown Portal */}
                      {openMenuId === p.id &&
                        createPortal(
                          <div
                            style={{
                              position: "fixed",
                              top: `${menuPosition.top}px`,
                              right: `${menuPosition.right}px`,
                              zIndex: 9999,
                            }}
                            className="w-56 bg-white border border-secondary-200 rounded-xl shadow-xl py-1 text-secondary-700 animate-in fade-in zoom-in-95 duration-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {features.has("clinical_record") && (
                              <button
                                onClick={() => { handleCloseMenu(); navigate(`/patient/${p.id}`); }}
                                className="w-full text-left px-4 py-2.5 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800 flex items-center gap-2.5 cursor-pointer"
                              >
                                <ClipboardList size={15} className="text-secondary-500" />
                                View record
                              </button>
                            )}
                            {p.patient_type !== "Virtual" && (
                              <button
                                onClick={() => { handleCloseMenu(); setChatPatient(p); }}
                                className="w-full text-left px-4 py-2.5 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800 flex items-center gap-2.5 cursor-pointer"
                              >
                                <MessagesSquare size={15} className="text-secondary-500" />
                                Messages
                              </button>
                            )}
                            <button
                              onClick={() => handlePatientEdit(p.id)}
                              className="w-full text-left px-4 py-2.5 hover:bg-secondary-50 transition-colors text-sm font-medium text-secondary-800 flex items-center gap-2.5 cursor-pointer"
                            >
                              <Edit3 size={15} className="text-secondary-500" />
                              Edit Patient
                            </button>
                            <button
                              onClick={() => handleOpenShareModal(p)}
                              className="w-full text-left px-4 py-2.5 hover:bg-primary-50 transition-colors text-sm font-medium text-primary-700 flex items-center gap-2.5 cursor-pointer"
                            >
                              <Share2 size={15} className="text-primary-600" />
                              Share Appointment Link
                            </button>
                          </div>,
                          document.body
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 bg-gray-50">
              <div className="text-sm text-secondary-500 font-medium">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 mt-4">
        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">Loading patients...</p>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-red-500">Error: {error}</p>
          </div>
        ) : patients.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-secondary-500 font-medium">No patient found.</p>
          </div>
        ) : (
          patients.map((p, index) => (
            <div
              key={p.id}
              className="bg-white shadow rounded-xl p-4 border border-secondary-200 space-y-3"
            >
              <div className="flex items-center justify-between border-b pb-2">
                <span className="text-xs text-secondary-500 font-medium">ID</span>
                <span className="text-sm font-semibold text-secondary-900">#{(currentPage - 1) * 20 + index + 1}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500 font-medium">Registration Date</span>
                <span className="text-sm text-secondary-900">{p.created}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500 font-medium">Patient Name</span>
                <span className="text-sm text-secondary-900 font-medium">{p.name} {p.risk_level && p.risk_level !== "low" && <span title="Current risk level" className={`ml-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.risk_level === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>{p.risk_level} risk</span>}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500 font-medium">Phone Number</span>
                <span className="text-sm text-secondary-900 font-medium">{p.contact || "-"}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500 font-medium">Address</span>
                <span className="text-sm text-secondary-900 text-right">{p.address || "-"}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary-500 font-medium">Patient Type</span>
                <span className="text-sm text-secondary-900">{p.patient_type}</span>
              </div>

              <div className="pt-2 border-t flex gap-2">
                {features.has("clinical_record") && (
                  <Button className="flex-1 text-xs !px-2 whitespace-nowrap" onClick={() => navigate(`/patient/${p.id}`)}>
                    <ClipboardList size={14} className="mr-1.5" /> Record
                  </Button>
                )}
                {p.patient_type !== "Virtual" && (
                  <Button className="flex-1 text-xs !px-2 whitespace-nowrap" onClick={() => setChatPatient(p)}>
                    <MessagesSquare size={14} className="mr-1.5" /> Messages
                  </Button>
                )}
                <Button
                  className="flex-1 text-xs !px-2 whitespace-nowrap"
                  onClick={() => handlePatientEdit(p.id)}
                  data-label="Edit Patient"
                >
                  <Edit3 size={14} className="mr-1.5" /> Edit
                </Button>
                <Button
                  className="flex-1 text-xs !px-2 whitespace-nowrap"
                  onClick={() => handleOpenShareModal(p)}
                >
                  <Share2 size={14} className="mr-1.5" /> Share link
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    
      <div className="md:hidden flex items-center justify-between mt-4 px-2">
        <div className="text-sm text-secondary-500 font-medium">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button 
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Shareable Appointment Link Modal */}
      {shareModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShareModal(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-secondary-200 animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100 bg-secondary-50/70">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center shadow-xs">
                  <Share2 size={18} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-secondary-900">Shareable Appointment Link</h3>
                  <p className="text-xs text-secondary-500">
                    Patient: <span className="font-semibold text-secondary-800">{shareModal.patient.name}</span>
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShareModal(null)}
                className="p-1.5 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors cursor-pointer"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="p-4 bg-primary-50/60 rounded-xl border border-primary-100 text-secondary-700 text-sm leading-relaxed">
                <p className="font-semibold text-primary-900 mb-1 flex items-center gap-1.5">
                  <Calendar size={15} className="text-primary-600" /> Appointment Booking Portal
                </p>
                <p className="text-xs text-secondary-600">
                  {shareModal.isSelfDoctor 
                    ? "This is your direct specialist booking link for this patient to book appointments with you."
                    : "Share this link with the patient to book an appointment with the selected clinician."}
                </p>
              </div>

              {/* Doctor Info or Selector */}
              {shareModal.isSelfDoctor ? (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-secondary-500 mb-1.5">
                    Clinician Profile
                  </label>
                  <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-xs">
                        <Stethoscope size={15} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-secondary-900">Dr. {shareModal.selectedDoctorName}</p>
                        <p className="text-xs text-secondary-500">Your profile link</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200">
                      Logged-in Doctor
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-secondary-600">
                      Selected Clinician
                    </label>
                    {shareModal.patient.has_last_doctor && shareModal.selectedDoctorHashid === shareModal.patient.doctor_hashid && (
                      <span className="text-[11px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded-full">
                        Last Visited Doctor
                      </span>
                    )}
                  </div>
                  <select
                    value={shareModal.selectedDoctorHashid || ""}
                    onChange={(e) => handleDoctorChange(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-secondary-300 text-sm text-secondary-900 bg-white focus:ring-2 focus:ring-primary-500 focus:outline-none transition-colors"
                  >
                    {availableDoctors.map((doc) => (
                      <option key={doc.doctor_hashid} value={doc.doctor_hashid}>
                        Dr. {doc.name} {doc.specialized ? `• ${doc.specialized}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-500 mb-1.5">
                  Shareable Link URL
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareModal.link}
                    className="flex-1 bg-secondary-50 border border-secondary-200 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm font-mono text-secondary-800 focus:outline-none select-all"
                  />
                  <button
                    onClick={handleCopyLink}
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer shadow-xs ${
                      copied 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-primary-600 hover:bg-primary-700 text-white'
                    }`}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Send it to the patient's own number */}
              <div className="pt-4 border-t border-secondary-100">
                <p className="text-xs font-bold uppercase tracking-wider text-secondary-500 mb-1.5">
                  Send to {shareModal.patient.name}
                </p>
                {shareModal.patient.contact ? (
                  <>
                    <p className="text-sm text-secondary-600 mb-2.5">{shareModal.patient.contact}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => handleSendLink("whatsapp")} disabled={sendingVia !== null}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50 cursor-pointer"
                        data-label="Send Link WhatsApp">
                        <MessageSquare size={16} /> {sendingVia === "whatsapp" ? "Opening…" : "WhatsApp"}
                      </button>
                      <button type="button" onClick={() => handleSendLink("sms")} disabled={sendingVia !== null || !shareModal.selectedDoctorHashid}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold disabled:opacity-50 cursor-pointer"
                        data-label="Send Link SMS">
                        <Send size={16} /> {sendingVia === "sms" ? "Sending…" : "Send SMS"}
                      </button>
                    </div>
                    <p className="text-xs text-secondary-500 mt-2">
                      WhatsApp opens your own WhatsApp with the message written. The SMS comes from MindConnect.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-secondary-500">
                    No mobile number on file. Add one with Edit Patient, or copy the link above.
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-secondary-50/70 border-t border-secondary-100 flex items-center justify-between">
              <a
                href={shareModal.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 transition-colors"
              >
                <ExternalLink size={14} /> Open Booking Page
              </a>
              <button
                onClick={() => setShareModal(null)}
                className="px-4 py-2 bg-secondary-200 hover:bg-secondary-300 text-secondary-800 font-medium rounded-xl text-xs transition-colors cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global backdrop to close dropdown menu */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleCloseMenu}
        />
      )}
      {chatPatient && <PatientChatModal patientId={chatPatient.id} patientName={chatPatient.name} onClose={() => setChatPatient(null)} />}
    </DashboardLayout>
  );
}
